// useTabStore.js
import { create } from "zustand";
import {
  getTabsConfig,
  getDefaultTabs,
  getLoggedOutTabs,
} from "@/utils/tabsConfig";
import { defaultWidgetSizes } from "@/utils/widgets";
import {
  breakpointCols,
  findFirstFitPosition,
  type BreakpointLayouts,
  type DashboardTab,
  type LayoutItem,
  type WidgetFilterData,
  type WidgetSearchData,
} from "@/utils/DashboardLayouts";
import { getUserTabs, saveUserTabsDebounced } from "@/shared/api/pref-storage";
import keycloakInstance from "@/initKeycloak";
import toast from "react-hot-toast";
import { isEqual } from "lodash";
import * as Sentry from "@sentry/react";
import { trackFeatureUsage } from "@/utils/gtm";
import { diagnosticEventLog } from "@/shared/services/diagnostic";

const hasInternalNewsquawkUserRole = () =>
  keycloakInstance.hasResourceRole("internal-newsquawk-user");

export type { DashboardTab };

const defaultActiveTab = "default";

// Initial tabs for logged-out users; replaced with server data on auth success
let defaultTabs: DashboardTab[] = getLoggedOutTabs();

export type TabStore = {
  tabs: DashboardTab[];
  activeTabId: string;
  tabsLoaded: boolean;
  /**
   * Session snapshot of "the server had no saved tabs at login" — the only
   * durable first-run signal. Must be captured here because the auto-persist
   * subscription writes the defaults back to the server on the first layout
   * nudge, destroying the empty-tabs state. Consumed by the onboarding gate.
   */
  serverTabsEmpty: boolean;
  resetToDefaults: () => void;
  switchTab: (tabId: string) => void;
  addTab: (title: string) => void;
  importTab: (
    title: string,
    enabledWidgets: string[],
    layout: BreakpointLayouts,
    widgetFilters?: Record<string, WidgetFilterData>
  ) => void;
  removeTab: (tabId: string) => void;
  updateLayout: (tabId: string, newLayout: BreakpointLayouts) => void;
  addWidget: (tabId: string, widgetCompositeId: string) => void;
  removeWidget: (tabId: string, widgetCompositeId: string) => void;
  renameTab: (tabId: string, newTitle: string) => void;
  resetLayouts: (tabId: string) => void;
  setWidgetFilter: (
    widgetId: string,
    selections: Record<string, string[]>
  ) => void;
  setWidgetHighlight: (
    widgetId: string,
    selections: Record<string, string[]>
  ) => void;
  setWidgetSearch: (widgetId: string, search: WidgetSearchData) => void;
};

const useTabStore = create<TabStore>()((set) => ({
  tabs: defaultTabs,

  activeTabId: defaultActiveTab,

  tabsLoaded: false,

  serverTabsEmpty: false,

  // Actions
  resetToDefaults: () =>
    set({ tabs: defaultTabs, activeTabId: defaultActiveTab }),

  /**
   * Switch to a different tab
   */
  switchTab: (tabId: string) => set({ activeTabId: tabId }),

  /**
   * Add a new tab with a given title
   */
  addTab: (title: string) =>
    set((state) => {
      const newTabId = `tab-${Date.now()}`;

      const defaultLayout = getTabsConfig(hasInternalNewsquawkUserRole())
        .default.layout;

      const newTab = {
        id: newTabId,
        title,
        layout: defaultLayout,
        enabledWidgets: [],
      };
      return { tabs: [...state.tabs, newTab], activeTabId: newTabId };
    }),

  /**
   * Import a tab with a given title, widgets, layout, and optional widget filters
   */
  importTab: (
    title: string,
    enabledWidgets: string[],
    layout: BreakpointLayouts,
    widgetFilters?: Record<string, WidgetFilterData>
  ) =>
    set((state) => {
      const newTabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
      const newTab: DashboardTab = {
        id: newTabId,
        title,
        enabledWidgets,
        layout,
        ...(widgetFilters && { widgetFilters }),
      };
      return { tabs: [...state.tabs, newTab], activeTabId: newTabId };
    }),

  /**
   * Remove a tab by its ID
   */
  removeTab: (tabId: string) =>
    set((state) => ({
      tabs: state.tabs.filter((tab) => tab.id !== tabId),
      activeTabId: state.activeTabId === tabId ? "default" : state.activeTabId,
    })),

  /**
   * Update the layout of a specific tab
   */
  updateLayout: (tabId: string, newLayout: BreakpointLayouts) =>
    set((state) => ({
      tabs: state.tabs.map((tab) =>
        tab.id === tabId ? { ...tab, layout: newLayout } : tab
      ),
    })),

  addWidget: (tabId: string, widgetCompositeId: string) => {
    trackFeatureUsage({
      feature_name: "widget",
      feature_action: "add",
      feature_detail: widgetCompositeId.split("-")[0],
    });
    set((state) => {
      const [widgetId] = widgetCompositeId.split("-");

      const updatedTabs = state.tabs.map((tab) => {
        if (tab.id !== tabId) return tab;

        const updatedEnabledWidgets = [
          ...(tab.enabledWidgets ?? []),
          widgetCompositeId,
        ];

        // Update the layout to include the new widget
        const updatedLayouts = { ...tab.layout };
        Object.keys(updatedLayouts).forEach((breakpoint) => {
          const layout =
            updatedLayouts[breakpoint as keyof typeof updatedLayouts] || [];

          if (!layout.find((item) => item.i === widgetCompositeId)) {
            const widgetSize =
              defaultWidgetSizes[widgetId as keyof typeof defaultWidgetSizes];
            if (!widgetSize) {
              console.error(
                `No default size found for widget "${widgetCompositeId}"`
              );
              return;
            }

            // Find optimal position using first-fit algorithm
            const cols = breakpointCols[breakpoint] || 16;
            const { x, y } = findFirstFitPosition(
              layout as LayoutItem[],
              widgetSize,
              cols
            );

            updatedLayouts[breakpoint as keyof typeof updatedLayouts] = [
              ...layout,
              { i: widgetCompositeId, ...widgetSize, x, y },
            ];
          }
        });

        return {
          ...tab,
          enabledWidgets: updatedEnabledWidgets,
          layout: updatedLayouts,
        };
      });

      return { tabs: updatedTabs };
    });
  },

  removeWidget: (tabId: string, widgetCompositeId: string) => {
    trackFeatureUsage({
      feature_name: "widget",
      feature_action: "remove",
      feature_detail: widgetCompositeId.split("-")[0],
    });
    set((state) => {
      const updatedTabs = state.tabs.map((tab) => {
        if (tab.id !== tabId) return tab;

        const updatedEnabledWidgets = tab.enabledWidgets?.filter(
          (id) => id !== widgetCompositeId
        );

        // Update the layout to remove the widget
        const updatedLayouts = { ...tab.layout };
        Object.keys(updatedLayouts).forEach((breakpoint) => {
          updatedLayouts[breakpoint as keyof typeof updatedLayouts] =
            updatedLayouts[breakpoint as keyof typeof updatedLayouts].filter(
              (item) => item.i !== widgetCompositeId
            );
        });

        // Clean up widget filter state
        const remainingFilters = { ...tab.widgetFilters };
        delete remainingFilters[widgetCompositeId];

        return {
          ...tab,
          enabledWidgets: updatedEnabledWidgets,
          layout: updatedLayouts,
          widgetFilters: Object.keys(remainingFilters).length > 0
            ? remainingFilters
            : undefined,
        };
      });

      return { tabs: updatedTabs };
    });
  },

  /**
   * Rename a user-created tab
   */
  renameTab: (tabId: string, newTitle: string) =>
    set((state) => ({
      tabs: state.tabs.map((tab) =>
        tab.id === tabId ? { ...tab, title: newTitle } : tab
      ),
    })),

  setWidgetFilter: (widgetId, selections) =>
    set((state) => ({
      tabs: state.tabs.map((tab) => {
        if (tab.id !== state.activeTabId) return tab;
        return {
          ...tab,
          widgetFilters: {
            ...tab.widgetFilters,
            [widgetId]: {
              ...tab.widgetFilters?.[widgetId],
              filter: selections,
            },
          },
        };
      }),
    })),

  setWidgetHighlight: (widgetId, selections) =>
    set((state) => ({
      tabs: state.tabs.map((tab) => {
        if (tab.id !== state.activeTabId) return tab;
        return {
          ...tab,
          widgetFilters: {
            ...tab.widgetFilters,
            [widgetId]: {
              ...tab.widgetFilters?.[widgetId],
              highlight: selections,
            },
          },
        };
      }),
    })),

  setWidgetSearch: (widgetId, search) =>
    set((state) => ({
      tabs: state.tabs.map((tab) => {
        if (tab.id !== state.activeTabId) return tab;
        return {
          ...tab,
          widgetFilters: {
            ...tab.widgetFilters,
            [widgetId]: {
              ...tab.widgetFilters?.[widgetId],
              search,
            },
          },
        };
      }),
    })),

  /**
   * Reset the layout and widget states of a specific tab to defaults
   */
  resetLayouts: (tabId: string) => {
    const tabsConfig = getTabsConfig(hasInternalNewsquawkUserRole());

    type TabConfigKey = keyof typeof tabsConfig;

    const config = tabsConfig[tabId as TabConfigKey];

    if (!config) return;

    set((state) => ({
      tabs: state.tabs.map((tab) => {
        if (tab.id !== tabId) return tab;

        return {
          ...tab,
          layout: config.layout,
          enabledWidgets: config.defaultWidgets,
        };
      }),
    }));
  },
}));

// Subscribe to store changes and persist to server
// Only persist if store is initialized
// and only if the tabs state has changed
useTabStore.subscribe(async (state, prevState) => {
  if (!state.tabsLoaded) {
    return;
  }

  // persist state to db
  if (!isEqual(state.tabs, prevState.tabs)) {
    console.log("Tabs state changed, saving to server:", state.tabs);
    saveUserTabsDebounced(state.tabs);
  }
});

/**
 * One-time migration: reads widget filter/highlight selections from localStorage
 * and moves them into the tab store's widgetFilters field.
 * Skips widgets that already have filter state in the store (from server).
 * Removes migrated entries from localStorage.
 */
function migrateLocalStorageFilters(tabs: DashboardTab[]): DashboardTab[] {
  let migrated = false;
  const updatedTabs = tabs.map((tab) => {
    const widgetFilters = { ...tab.widgetFilters };
    for (const widgetId of tab.enabledWidgets) {
      // Skip if this widget already has filter state in the store
      if (widgetFilters[widgetId]) continue;

      const filterRaw = localStorage.getItem(widgetId);
      const highlightRaw = localStorage.getItem(`${widgetId}-highlight`);
      if (!filterRaw && !highlightRaw) continue;

      const entry: WidgetFilterData = {};
      try {
        if (filterRaw) entry.filter = JSON.parse(filterRaw);
      } catch {
        // invalid JSON — skip
      }
      try {
        if (highlightRaw) entry.highlight = JSON.parse(highlightRaw);
      } catch {
        // invalid JSON — skip
      }

      if (entry.filter || entry.highlight) {
        widgetFilters[widgetId] = entry;
        migrated = true;
      }
      // Remove from localStorage
      localStorage.removeItem(widgetId);
      localStorage.removeItem(`${widgetId}-highlight`);
    }
    return { ...tab, widgetFilters };
  });
  return migrated ? updatedTabs : tabs;
}

// Record tab/widget changes for diagnostic reports
useTabStore.subscribe((state, prevState) => {
  if (state.activeTabId !== prevState.activeTabId) {
    const tab = state.tabs.find((t) => t.id === state.activeTabId);
    diagnosticEventLog.record("tabs:widget", "info", "tab-switch", {
      tabId: state.activeTabId,
      widgetCount: tab?.enabledWidgets?.length ?? 0,
    });
  }

  if (!isEqual(state.tabs, prevState.tabs)) {
    const activeTab = state.tabs.find((t) => t.id === state.activeTabId);
    const prevActiveTab = prevState.tabs.find((t) => t.id === state.activeTabId);
    const currentWidgets = activeTab?.enabledWidgets ?? [];
    const prevWidgets = prevActiveTab?.enabledWidgets ?? [];

    if (!isEqual(currentWidgets, prevWidgets)) {
      diagnosticEventLog.record("tabs:widget", "info", "widgets-changed", {
        tabId: state.activeTabId,
        widgetCount: currentWidgets.length,
        widgets: currentWidgets,
      });
    }
  }
});

// Load initial tab state from server
// Only if Keycloak is authenticated
keycloakInstance.onAuthSuccess = async () => {
  // User has logged in,
  // Compute default tabs based on user role
  defaultTabs = getDefaultTabs(hasInternalNewsquawkUserRole());

  useTabStore.setState({
    tabs: defaultTabs,
  });

  try {
    const serverTabs: DashboardTab[] | undefined = await getUserTabs();
    if (serverTabs && serverTabs.length > 0) {
      // Migrate any widget filter selections from localStorage into the tab data.
      // Set tabs + tabsLoaded together so the subscription sees both changes
      // in one update — otherwise migrated filters won't be saved to server.
      const tabs = migrateLocalStorageFilters(serverTabs);
      useTabStore.setState({ tabs, tabsLoaded: true });
      toast.success("Tab layouts successfully loaded from server");
    } else {
      // No saved tabs on the server — first ever login (or a full reset).
      // Snapshot the signal before the auto-persist subscription can write
      // the defaults back and destroy it.
      useTabStore.setState({ tabsLoaded: true, serverTabsEmpty: true });
    }
  } catch (error) {
    console.error("Error loading tabs from server:", error);
    toast.error("Failed to load Tab layouts from server");
    Sentry.captureException(error);
    useTabStore.setState({ tabsLoaded: true });
  }
};

export default useTabStore;
